import { ref } from '@nuxtjs/composition-api';

type SearchHistoryOptions = {
  hasStorageConsent: () => boolean;
  onChange: (history: string[]) => void;
};

const SEARCH_HISTORY_KEY = 'searchHistory';

export const useSearchHistory = ({
  hasStorageConsent,
  onChange,
}: SearchHistoryOptions) => {
  const searchHistory = ref<string[]>([]);

  const getStorage = (): Storage | null => {
    if (typeof window === 'undefined') return null;

    try {
      if (!hasStorageConsent()) {
        window.localStorage.removeItem(SEARCH_HISTORY_KEY);
        window.sessionStorage.removeItem(SEARCH_HISTORY_KEY);
        return null;
      }

      return window.localStorage;
    } catch (error) {
      console.error('Failed to access search history storage', error);
      return null;
    }
  };

  const getStoredHistory = (): string[] => {
    try {
      const storage = getStorage();
      if (!storage) return [];

      const storedHistory = storage.getItem(SEARCH_HISTORY_KEY);

      return storedHistory ? JSON.parse(storedHistory) : [];
    } catch (error) {
      console.error('Failed to parse search history', error);
      return [];
    }
  };

  const persistHistory = (history: string[]) => {
    const storage = getStorage();
    if (!storage) return;

    storage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));

    if (storage === window.localStorage) {
      window.sessionStorage.removeItem(SEARCH_HISTORY_KEY);
    }
  };

  const publishHistory = (history: string[]) => {
    searchHistory.value = history;
    onChange(history);
  };

  const syncSearchHistory = () => {
    publishHistory(getStoredHistory());
  };

  const updateSearchHistory = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue || typeof window === 'undefined') return;

    if (!getStorage()) {
      publishHistory([]);
      return;
    }

    const normalizedValue = trimmedValue.toLowerCase();
    const updatedHistory = [
      trimmedValue,
      ...getStoredHistory().filter(
        (item) => item.toLowerCase() !== normalizedValue
      ),
    ].slice(0, 5);

    persistHistory(updatedHistory);
    publishHistory(updatedHistory);
  };

  const removeSearchHistoryTerm = (value: string) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue) return;

    if (!getStorage()) {
      publishHistory([]);
      return;
    }

    const updatedHistory = getStoredHistory().filter(
      (item) => item.trim().toLowerCase() !== normalizedValue
    );

    persistHistory(updatedHistory);
    publishHistory(updatedHistory);
  };

  return {
    removeSearchHistoryTerm,
    searchHistory,
    syncSearchHistory,
    updateSearchHistory,
  };
};
